/**
 * Sentinel Licensing framework — 6 tiers + Organizational Certification.
 *
 * Public-facing licensing content, pricing structure, and tier definitions.
 * Used by the Licensing page and the license management UI.
 */

export type LicenseTierId =
  | "use"
  | "architecture"
  | "template"
  | "certification"
  | "automation"
  | "vendor_integration"
  | "organizational";

export type PricingMode = "public" | "quote";

export interface LicenseTierPricing {
  label: string;
  price: number | null;
  period: string;
  mode: PricingMode;
}

export interface LicenseTier {
  id: LicenseTierId;
  number: number | null;
  name: string;
  tagline: string;
  description: string;
  covers: string[];
  whoNeedsIt: string;
  scenario: { title: string; body: string };
  pricing: LicenseTierPricing[];
  priceFactors: string[];
  ctaLabel: string;
  ctaTo: string;
  icon: string;
}

export const LICENSE_TIERS: LicenseTier[] = [
  {
    id: "use",
    number: 1,
    name: "Sentinel Use License",
    tagline: "The foundational license required for any organization using Sentinel.",
    description:
      "Every organization using Sentinel in any form — manual, digital, or automated — must hold a Sentinel Use License. This grants access to the Sentinel Portal, workflows, domain profiles, reporting structure, and manual checklists.",
    covers: [
      "Sentinel Portal access",
      "Sentinel workflows",
      "Sentinel domain profiles",
      "Sentinel reporting structure",
      "Manual Sentinel checklists",
    ],
    whoNeedsIt: "Any organization using Sentinel in any form.",
    scenario: {
      title: "Hotel with 200 employees — only 25 use Sentinel",
      body: "A hotel has 200 employees total, but only 10 supervisors, 5 managers, and 10 safety operators need Sentinel access. They buy a 25-seat Small Teams license — NOT a 200-seat license — because only 25 people actually operate Sentinel. The remaining 175 employees never log in and don't need seats.",
    },
    pricing: [
      { label: "Small Teams (1–25 users)", price: 1500, period: "/ year", mode: "public" },
      { label: "Departments (25–100 users)", price: 4000, period: "/ year", mode: "public" },
      { label: "Organizations", price: null, period: "", mode: "quote" },
    ],
    priceFactors: ["Number of users", "Number of departments", "Reporting needs"],
    ctaLabel: "Get Sentinel Use License",
    ctaTo: "/licensing/quote?tier=use",
    icon: "portal",
  },
  {
    id: "architecture",
    number: 2,
    name: "Sentinel Architecture License",
    tagline: "Grants legal rights to use the patented Sentinel architecture.",
    description:
      "Organizations implementing Sentinel logic — manually or digitally — need the Architecture License. This grants legal rights to use the patented diagnostic logic, intervention logic, verification logic, multi-domain safety architecture, and Sentinel workflow methodology.",
    covers: [
      "Diagnostic logic",
      "Intervention logic",
      "Verification logic",
      "Multi-domain safety architecture",
      "Sentinel workflow methodology",
    ],
    whoNeedsIt: "Organizations implementing Sentinel logic manually or digitally.",
    scenario: {
      title: "Manufacturing plant — 3 production lines, 40 operators",
      body: "A manufacturing plant runs 3 production lines with 40 operators across two shifts. They implement Sentinel's diagnostic and intervention logic on the factory floor. Since 40 operators fall in the 25–100 range, they buy the Departments license at $9,500/year. They do NOT need the Automation License because they run Sentinel manually (paper checklists + portal), not via automated sensors.",
    },
    pricing: [
      { label: "Small Teams (1–25 users)", price: 3500, period: "/ year", mode: "public" },
      { label: "Departments (25–100 users)", price: 9500, period: "/ year", mode: "public" },
      { label: "Organizations", price: null, period: "", mode: "quote" },
    ],
    priceFactors: ["Industry risk profile", "Number of workflows", "Architecture usage depth"],
    ctaLabel: "Get Architecture License",
    ctaTo: "/licensing/quote?tier=architecture",
    icon: "architecture",
  },
  {
    id: "template",
    number: 3,
    name: "Sentinel Template License",
    tagline: "Allows organizations to use and customize Sentinel's copyrighted templates.",
    description:
      "Organizations that need to customize Sentinel for internal processes require the Template License. This covers Sentinel templates, custom template creation, uploading custom versions, template-based compliance, and template-based certification.",
    covers: [
      "Sentinel templates",
      "Custom template creation",
      "Uploading custom versions",
      "Template-based compliance",
      "Template-based certification",
    ],
    whoNeedsIt: "Organizations customizing Sentinel for internal processes.",
    scenario: {
      title: "Hospital network — 5 facilities, custom safety checklists",
      body: "A hospital network with 5 facilities needs to customize Sentinel's standard safety templates for their internal infection-control protocols. They create custom templates, upload them to the portal, and assign them across departments. With 30 users editing and using templates, they buy the Departments license at $3,000/year. They also hold a Use License (required for portal access).",
    },
    pricing: [
      { label: "Small Teams (1–25 users)", price: 1200, period: "/ year", mode: "public" },
      { label: "Departments (25–100 users)", price: 3000, period: "/ year", mode: "public" },
      { label: "Organizations", price: null, period: "", mode: "quote" },
    ],
    priceFactors: ["Number of templates", "Customization level", "Department count"],
    ctaLabel: "Get Template License",
    ctaTo: "/licensing/quote?tier=template",
    icon: "template",
  },
  {
    id: "certification",
    number: 4,
    name: "Sentinel Certification License",
    tagline: "Required for individuals or organizations participating in Sentinel certification programs.",
    description:
      "Individuals, departments, or organizations seeking Sentinel certification need this license. It covers certification testing, compliance audits, Sentinel Certified branding, annual recertification, and audit logs with compliance reports.",
    covers: [
      "Certification testing",
      "Compliance audits",
      "Sentinel Certified branding",
      "Annual recertification",
      "Audit logs and compliance reports",
    ],
    whoNeedsIt: "Individuals, departments, or organizations seeking Sentinel certification.",
    scenario: {
      title: "Construction company — certifying 60 workers across 3 departments",
      body: "A construction company wants 60 workers certified across 3 departments (Site Safety, Equipment Operations, Compliance). They buy the Certification License at $2,500/year per department = $7,500/year total. Each certified worker gets a Sentinel certification badge, and the company gets audit logs for compliance reporting. They do NOT need to certify all 200 employees — only the 60 who hold safety roles.",
    },
    pricing: [
      { label: "Per Department (25–100 users)", price: 2500, period: "/ year", mode: "public" },
      { label: "Organization-wide", price: null, period: "", mode: "quote" },
    ],
    priceFactors: ["Number of certified personnel", "Number of departments", "Audit scope"],
    ctaLabel: "Get Certification License",
    ctaTo: "/licensing/quote?tier=certification",
    icon: "certification",
  },
  {
    id: "automation",
    number: 5,
    name: "Sentinel Automation License",
    tagline: "Required for organizations implementing Level 2 automation using Sentinel's automated safety engine.",
    description:
      "Organizations deploying automated Sentinel systems — the Safety Engine, diagnostic engine, intervention engine, verification engine, automated reporting, sensor/IoT integration, and automated workflow execution — need the Automation License.",
    covers: [
      "Sentinel Safety Engine",
      "Diagnostic engine",
      "Intervention engine",
      "Verification engine",
      "Automated reporting",
      "Sensor + IoT integration",
      "Automated workflow execution",
    ],
    whoNeedsIt: "Organizations deploying automated Sentinel systems.",
    scenario: {
      title: "Oil refinery — IoT sensors + automated safety engine",
      body: "An oil refinery deploys IoT sensors across 200 pressure vessels, feeding real-time data into Sentinel's automated Safety Engine. The Safety Engine runs diagnostic, intervention, and verification logic without human intervention. Because this is Level 2 automation (not manual), they need the Automation License. Pricing is quote-based because it depends on sensor count, integration complexity, and the number of automated workflows. They also hold Use + Architecture licenses.",
    },
    pricing: [
      { label: "Automation licensing is quote-based", price: null, period: "", mode: "quote" },
    ],
    priceFactors: [
      "Integration complexity",
      "IoT requirements",
      "Sensor networks",
      "Workflow automation depth",
      "OEM involvement",
    ],
    ctaLabel: "Request Automation Consultation",
    ctaTo: "/licensing/quote?tier=automation",
    icon: "automation",
  },
  {
    id: "vendor_integration",
    number: 6,
    name: "Sentinel Vendor Integration License",
    tagline: "Required for vendors building Sentinel-compatible software or hardware.",
    description:
      "Software vendors, IoT vendors, robotics companies, automation platforms, and OEMs building Sentinel-compatible products need the Vendor Integration License. It covers integration with the Sentinel Portal, use of Sentinel architecture inside vendor products, ability to sell Sentinel-compatible solutions, and OEM-level integration rights.",
    covers: [
      "Integration with Sentinel Portal",
      "Use of Sentinel architecture inside vendor products",
      "Ability to sell Sentinel-compatible solutions",
      "OEM-level integration rights",
    ],
    whoNeedsIt: "Software vendors, IoT vendors, robotics companies, automation platforms, OEMs.",
    scenario: {
      title: "IoT sensor vendor — building Sentinel-compatible devices",
      body: "A company manufactures IoT pressure sensors for industrial tanks. They want their sensors to integrate directly with the Sentinel Portal and market them as 'Sentinel-Compatible.' They need the Vendor Integration License, which grants the right to use Sentinel's architecture inside their product and sell Sentinel-compatible solutions. Pricing is quote-based because it depends on product type, integration depth, and whether OEM-level rights are needed.",
    },
    pricing: [
      { label: "Vendor pricing is quote-based", price: null, period: "", mode: "quote" },
    ],
    priceFactors: ["Product type", "Integration depth", "Architecture usage", "OEM licensing requirements"],
    ctaLabel: "Explore Vendor Integration",
    ctaTo: "/vendor-integration",
    icon: "vendor",
  },
  {
    id: "organizational",
    number: null,
    name: "Sentinel Organizational Certification",
    tagline: "The highest-level certification — awarded to an entire company, agency, or organization.",
    description:
      "Organizational Certification is not a license tier — it is the highest-level Sentinel certification, awarded to an entire organization. It verifies company-wide Sentinel implementation, department-level compliance, reporting structure, template usage, annual audits, certified personnel in required roles, and grants the Sentinel Certified Organization badge.",
    covers: [
      "Company-wide Sentinel implementation",
      "Department-level compliance",
      "Sentinel reporting structure",
      "Sentinel template usage",
      "Annual audits",
      "Certified personnel in required roles",
      "Enterprise compliance dashboard",
      "Sentinel Certified Organization badge",
    ],
    whoNeedsIt: "Organizations seeking enterprise-level Sentinel compliance and licensing.",
    scenario: {
      title: "National logistics company — 12 warehouses, 500 employees",
      body: "A national logistics company with 12 warehouses and 500 employees wants full Sentinel Organizational Certification. They already hold Use, Architecture, and Template licenses across departments. To earn the 'Sentinel Certified Organization' badge, they undergo a company-wide audit verifying every warehouse meets Sentinel standards, all required roles have certified personnel, and reporting structures are in place. Pricing is quote-based because it depends on facility count, audit scope, and industry risk profile.",
    },
    pricing: [
      { label: "Organizational Certification is quote-based", price: null, period: "", mode: "quote" },
    ],
    priceFactors: [
      "Number of departments",
      "Number of certified personnel",
      "Audit scope",
      "Industry risk profile",
      "Automation level",
      "Compliance requirements",
    ],
    ctaLabel: "Become a Sentinel Certified Organization",
    ctaTo: "/licensing/quote?tier=organizational",
    icon: "organization",
  },
];

export interface LicensingAudience {
  title: string;
  items: string[];
}

export const LICENSING_AUDIENCES: LicensingAudience[] = [
  {
    title: "Individual Users",
    items: [
      "Core Certification",
      "Industry Certifications",
      "Professional Certifications",
      "Instructor / Auditor / Architect tracks",
    ],
  },
  {
    title: "Departments",
    items: [
      "Department-level licensing",
      "Department budgets",
      "Department admins",
      "Department-specific certifications",
      "Department-specific compliance dashboards",
    ],
  },
  {
    title: "Entire Organizations",
    items: [
      "Enterprise licensing",
      "Organizational Certification",
      "Multi-department oversight",
      "Enterprise compliance dashboards",
      "Enterprise reporting",
    ],
  },
  {
    title: "Vendors & OEMs",
    items: [
      "Vendor Integration License",
      "OEM Architecture License",
      "Automation License",
      "API access",
      "Integration rights",
    ],
  },
  {
    title: "Government & Municipal Agencies",
    items: [
      "Multi-department licensing",
      "Agency-wide certification",
      "Compliance reporting",
      "Annual audits",
    ],
  },
];

export interface PricingSection {
  title: string;
  description: string;
  items: string[];
  ctaLabel: string;
  ctaTo: string;
  isQuoteBased?: boolean;
}

export const PRICING_SECTIONS: PricingSection[] = [
  {
    title: "Individual Pricing",
    description: "Clear, fixed pricing for individual certifications.",
    items: [
      "Core Certification",
      "Industry Certifications",
      "Professional Certifications",
    ],
    ctaLabel: "View Certifications",
    ctaTo: "/certifications",
  },
  {
    title: "Group Pricing",
    description: "Tiered pricing for teams and departments.",
    items: [
      "5 seats — $2,475 / year",
      "10 seats — $4,455 / year (10% savings)",
      "25 seats — $8,950 / year (20% savings)",
      "50 seats — $24,950 / year (Enterprise)",
    ],
    ctaLabel: "View Group Plans",
    ctaTo: "/certifications",
  },
  {
    title: "Enterprise Licensing",
    description: "Pricing varies by industry, department count, seat count, automation level, and integration scope.",
    items: [
      "Industry-specific pricing",
      "Department-based pricing",
      "Automation-based pricing",
      "Integration complexity",
      "Compliance requirements",
      "Audit scope",
    ],
    ctaLabel: "Request Enterprise Consultation",
    ctaTo: "/licensing/quote?tier=enterprise",
    isQuoteBased: true,
  },
  {
    title: "Vendor & OEM Licensing",
    description: "Pricing based on integration depth, architecture usage, product type, and compliance requirements.",
    items: [
      "Integration depth",
      "Architecture usage",
      "Product type",
      "Compliance requirements",
    ],
    ctaLabel: "Explore Vendor Integration",
    ctaTo: "/vendor-integration",
    isQuoteBased: true,
  },
];

export interface GroupSeatPlan {
  id: string;
  name: string;
  seats: number;
  price: number;
  priceId: string;
  type: "agency" | "enterprise";
  description: string;
  features: string[];
  popular?: boolean;
}

export const GROUP_SEAT_PLANS: GroupSeatPlan[] = [
  {
    id: "agency-5",
    name: "Agency Plan — 5 Seats",
    seats: 5,
    price: 2475,
    priceId: "price_1U03tMD2TH4f4lLQH0U6Y7qD",
    type: "agency",
    description: "Perfect for small teams and field offices",
    features: ["5 seats across all levels", "All 4 certification levels", "Annual renewal included", "Seat management dashboard", "Progress tracking"],
  },
  {
    id: "agency-10",
    name: "Agency Plan — 10 Seats",
    seats: 10,
    price: 4455,
    priceId: "price_1U03tMD2TH4f4lLQA3lawxI8",
    type: "agency",
    description: "Great for growing departments",
    features: ["10 seats across all levels", "All 4 certification levels", "Annual renewal included", "Seat management dashboard", "Progress tracking", "10% savings vs individual"],
    popular: true,
  },
  {
    id: "agency-25",
    name: "Agency Plan — 25 Seats",
    seats: 25,
    price: 8950,
    priceId: "price_1U03tMD2TH4f4lLQxhGGPV5g",
    type: "agency",
    description: "For mid-size agencies and precincts",
    features: ["25 seats across all levels", "All 4 certification levels", "Annual renewal included", "Seat management dashboard", "Progress tracking", "Bulk analytics reporting", "20% savings vs individual"],
  },
  {
    id: "enterprise-50",
    name: "Enterprise Plan — 50 Seats",
    seats: 50,
    price: 24950,
    priceId: "price_1U03tND2TH4f4lLQQtLDyIP8",
    type: "enterprise",
    description: "For large organizations and multi-site deployments",
    features: ["50 seats across all levels", "All 4 certification levels", "Annual renewal included", "Advanced seat management", "Org-wide analytics", "Custom branding", "Priority support", "Dedicated account manager"],
  },
];

export function getTierById(id: LicenseTierId): LicenseTier | undefined {
  return LICENSE_TIERS.find((t) => t.id === id);
}
